<script setup lang="ts">
import BaseLogo from "@/core/components/logo/BaseLogo.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import useLaboratory from "@/laboratory/composables/useLaboratory";
import {computed, nextTick, onMounted, onUnmounted, ref} from "vue";
import LaboratoryWelcome from "@/laboratory/components/LaboratoryWelcome.vue";
import useLaboratoryStatistics from "@/laboratory/composables/useLaboratoryStatistics";
import {animate} from "motion";
import {useLaboratoriesStore} from "@/laboratory/stores/laboratories.store";
import {storeToRefs} from "pinia";
import LaboratoryNavigationMenu from "@/laboratory/components/LaboratoryNavigationMenu.vue";
import useLaboratoryRoutines from "@/laboratory/composables/useLaboratoryRoutines";
import useLaboratoryOffers from "@/laboratory/composables/useLaboratoryOffers";
import {useSystemStore} from "@/stores/system.store";
import {laboratoryApi, shopApi} from "@/container";
import {useRouter} from "vue-router";
import {useCartStore} from "@/laboratory/stores/cart.store";
import LaboratoryFrancoBar from "@/laboratory/components/LaboratoryFrancoBar.vue";
import LaboratoryCartButton from "@/laboratory/components/LaboratoryCartButton.vue";

const props = defineProps<{
    id: string;
}>();
const displayContent = ref(false);
const isGradientShown = ref(false);

const {laboratories} = storeToRefs(useLaboratoriesStore());
const {data} = useLaboratory(props.id);
const {environment} = useSystemStore();
const healthcenterId = environment?.entityId;
const {prefetch: prefetchStatistics} = useLaboratoryStatistics(props.id);
const {prefetch: prefetchRoutines} = useLaboratoryRoutines(props.id);
const {prefetch: prefetchOffers} = useLaboratoryOffers(props.id);
const {cart, franco} = storeToRefs(useCartStore());
const router = useRouter();

/**
 * On mount, fetch data.
 */
onMounted(() => {
    prefetchStatistics();
    prefetchRoutines();
    prefetchOffers();

    if (healthcenterId) {
        shopApi().laboratoryCart(Number(props.id), healthcenterId)
            .then(response => {
                cart.value = response;
            });
        laboratoryApi().franco(Number(props.id), healthcenterId)
            .then(response => {
                franco.value = response;
            });
    }
});

/**
 * Handle when welcome animation is finished.
 */
const handleWelcomeAnimationFinished = async () => {
    displayContent.value = true;
    await nextTick();

    animate(".content-container", {'--gradient-opacity': [0, 1]}, {duration: 0.4})
        .then(() => {
            isGradientShown.value = true;
        });

    animate([
        [".laboratory-header", {y: ["-100%", "0%"]}, {duration: 1, ease: "easeInOut"}],
        [".laboratory-content", {opacity: [0, 1], y: ["20px", "0px"]}, {duration: 0.4, at: "-0.2"}]
    ]);
};

/**
 * On unmounted.
 */
onUnmounted(() => {
    isGradientShown.value = false;
});

const laboratory = computed(() => data.value ?? laboratories.value[props.id] ?? null);

/**
 * Display cart.
 */
const displayCart = () => {
    router.push({
        name: 'laboratory.cart',
        params: {laboratoryId: props.id}
    });
};
</script>

<template>
  <BaseFlexSpinner v-if="!laboratory" />
  <div
    v-if="laboratory"
    class="h-full overflow-auto content-container"
    :style="{
      '--gradient-opacity': isGradientShown ? 1 : 0,
      backgroundImage: laboratory?.branding?.background
        ? `linear-gradient(to top, rgb(255, 255, 255, var(--gradient-opacity)) 0%, rgba(255, 255, 255, 0) 80%), url(${laboratory.branding.background.url})`
        : '',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }"
  >
    <template v-if="displayContent">
      <div
        class="relative bg-white items-center laboratory-header"
        style="transform: translateY(-100%);"
      >
        <div class="flex justify-between gap-4">
          <div class="bg-white pt-4 px-2 space-y-6">
            <div class="space-y-2">
              <h1 class="text-black">
                Gestion de la relation patient {{ laboratory.name }}
              </h1>
              <div class="border-l-2 border-black pl-3">
                Retrouvez tous les outils {{ laboratory.name }} pour vous aider avec vos patients.
              </div>
            </div>
          </div>
          <div class="flex justify-end pr-8 shrink-0">
            <BaseLogo alt="logo" :logo="laboratory?.logo" img-class="object-contain" class="bg-white !h-24" />
          </div>
        </div>
        <div class="p-2">
          <div
            class="bg-white flex items-start lg:items-center justify-between gap-4"
          >
            <div class="flex-1 space-y-2">
              <LaboratoryNavigationMenu
                :active-color="laboratory?.branding?.primary_color"
                :active-text-color="laboratory?.branding?.secondary_color"
              />
            </div>
            <div
              v-if="franco"
              class="flex flex-col-reverse md:flex-row flex-wrap-reverse items-end md:justify-end md:items-center gap-x-4 gap-y-2 pr-6"
            >
              <LaboratoryFrancoBar :franco="franco" :cart="cart" :laboratory="laboratory" class="hidden lg:block w-72" />
              <LaboratoryCartButton :cart="cart" :laboratory="laboratory" @display-cart="displayCart()" />
            </div>
          </div>
          <LaboratoryFrancoBar v-if="franco" :franco="franco" :cart="cart" :laboratory="laboratory" class="lg:hidden w-full mt-2" />
        </div>
      </div>
      <div class="laboratory-content" style="opacity: 0; transform: translateY(20px)">
        <router-view :laboratory-id="props.id" :laboratory="laboratory" />
      </div>
    </template>
    <div v-else class="w-full overflow-hidden">
      <LaboratoryWelcome class="mt-32" :laboratory="laboratory" @animation-finished="handleWelcomeAnimationFinished" />
    </div>
  </div>
</template>
